import Image from "next/image";
import { SbImageWithMeta } from "./types";
import styled, { css } from "styled-components";
import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { GalleryThumb } from "@/components/ProductPage/ProductPageHero/GalleryThumb";
import NextLazyImageFuture from "@/components/Core/NextLazyImageFuture";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import mq from "@/utils/mq";
import GalleryThumbnails from "@/components/ProductPage/ProductPageHero/GalleryThumbnails";
import ChevronRight from "@/assets/svg/chevronRight.svg";
import { useWindowSize } from "@uidotdev/usehooks";

interface ProductPageGalleryProps {
  images?: SbImageWithMeta[];
  imageURL?: string;
  description?: string;
  hasTransparentBackground?: boolean;
}

const GalleryOuterContainer = styled.div`
  display: flex;
  gap: 8px;
  @media screen and (max-width: 1000px) {
    flex-direction: column-reverse;
  }
`;

const CarouselWrapper = styled.div`
  overflow: hidden;
  position: relative;
  overscroll-behavior-x: contain;
  width: 100%;
  will-change: transform;
  aspect-ratio: 2/2.55;
  border-radius: 5px;
  @media (max-width: 1000px) {
    border-radius: 0;
  }
`;

const CarouselContainer = styled.div`
  display: flex;
  height: 100%;
  margin-bottom: 20px;
  will-change: transform;
  @media (max-width: 1000px) {
    gap: 0;
  }
`;

const GallerySlide = styled.div`
  flex: 0 0 100%;
  position: relative;
  margin-right: 16px;
  width: 100%;
  height: 100%;
  will-change: transform;
  border-radius: 5px;
  overflow: hidden;
  @media (max-width: 1000px) {
    border-radius: 0;
  }
`;

const ControlsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 12px;
  z-index: 2;
`;

const ControlButton = styled.button`
  appearance: none;
  background-color: rgba(255, 255, 255, 0.3);
  border: none;
  outline: none;
  margin: 0;
  padding: 0;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.15s ease-out;
  color: #000;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.04);
  &[data-flip] {
    transform: rotate(180deg);
  }
  &:hover {
    background-color: rgba(255, 255, 255, 1);
    border: 1px solid rgba(0, 0, 0, 0.2);
    transition: all 0.15s ease-out;
  }

  &:active {
    transform: scale(0.97);
    &[data-flip] {
      transform: scale(0.97) rotate(180deg);
    }
  }
  svg {
    width: 20px;
    height: 20px;
  }
`;

export default function ProductPageGallery({
  imageURL = "",
  images,
  description = "Product image",
}: ProductPageGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [imageCollection, setImageCollection] = useState([{ filename: imageURL, alt: description }, ...images]);
  const { width } = useWindowSize();
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel(
    {
      loop: true,
      align: width < 1000 ? "start" : "center",
      skipSnaps: true,
      inViewThreshold: 0.2,
    },
    [
      WheelGesturesPlugin({
        forceWheelAxis: "x",
      }),
    ],
  );

  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    dragFree: true,
    align: "start",
  });

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return;
    setSelectedIndex(emblaMainApi.selectedScrollSnap());
    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
  }, [emblaMainApi, emblaThumbsApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaMainApi) return;
    onSelect();

    emblaMainApi.on("select", onSelect).on("reInit", onSelect);
  }, [emblaMainApi, onSelect]);

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) return;
      emblaMainApi.scrollTo(index);
    },
    [emblaMainApi, emblaThumbsApi],
  );

  return (
    <GalleryOuterContainer>
      <GalleryThumbnails
        imageCollection={imageCollection}
        onThumbClick={onThumbClick}
        emblaThumbsRef={emblaThumbsRef}
        selectedIndex={selectedIndex}
      />
      <CarouselWrapper ref={emblaMainRef}>
        <CarouselContainer>
          {imageCollection &&
            imageCollection.map((image, index) => (
              <GallerySlide key={index}>
                <NextLazyImageFuture fill src={image.filename} alt={image.alt} className='gallery_img' />
              </GallerySlide>
            ))}
        </CarouselContainer>
        <ControlsWrapper>
          <ControlButton data-flip onClick={() => emblaMainApi?.scrollPrev()}>
            <ChevronRight />
          </ControlButton>
          <ControlButton onClick={() => emblaMainApi?.scrollNext()}>
            <ChevronRight />
          </ControlButton>
        </ControlsWrapper>
      </CarouselWrapper>
    </GalleryOuterContainer>
  );
}
